/**
 * DEV ONLY — Auto-bootstraps a mock authenticated user so you can navigate
 * directly to any screen without going through the login flow.
 *
 * Change DEV_ACTIVE_PROFILE to switch between mock users:
 *
 *   "vendor-landing"       → vendor, pending_registration  → Vendor Landing Page
 *   "vendor-in-progress"   → vendor, pending_application   → Pre-Vetting Step 1
 *   "vendor-pending-review"→ vendor, pending_review        → Pending Review screen
 *   "vendor-approved"      → vendor, approved              → Approved screen
 *   "vendor-rejected"      → vendor, rejected              → Rejected screen
 *   "staff"                → staff                         → Staff review dashboard
 */

import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import type { AuthUser } from "@/context/AuthContext";
import type { VendorStatus, UserRole } from "@chinese-vendor/schemas";
import { useSegments } from "expo-router";

// ─── ✏️  CHANGE THIS to switch active dev profile ────────────────────────────
const DEV_ACTIVE_PROFILE = "vendor-approved" as
  | "vendor-landing"
  | "vendor-in-progress"
  | "vendor-pending-review"
  | "vendor-approved"
  | "vendor-rejected"
  | "staff";
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Set to true by `suppressDevAuth()` when the user explicitly presses the
 * dev logout button. Prevents the bootstrap from immediately re-injecting a
 * session after logout. Resets on full app reload (module re-evaluation).
 */
let devAuthSuppressed = false;

/** Call this before `logout()` to stop DevAuthBootstrap from re-firing. */
export function suppressDevAuth() {
  devAuthSuppressed = true;
}

type DevProfile = {
  user: AuthUser;
  token: string;
};

const DEV_PROFILES: Record<typeof DEV_ACTIVE_PROFILE, DevProfile> = {
  "vendor-landing": {
    token: "dev-token-vendor-landing",
    user: {
      id: "dev-vendor-001",
      email: "vendor-new@example.com",
      firstName: "Wei",
      lastName: "Zhang",
      roles: ["vendor"] as UserRole[],
      vendorStatus: "pending_registration" as VendorStatus,
      applicationId: null,
      temporaryPasswordSet: false,
    },
  },
  "vendor-in-progress": {
    token: "dev-token-vendor-progress",
    user: {
      id: "dev-vendor-002",
      email: "vendor-wip@example.com",
      firstName: "Li",
      lastName: "Wang",
      roles: ["vendor"] as UserRole[],
      vendorStatus: "pending_application" as VendorStatus,
      applicationId: null,
      temporaryPasswordSet: false,
    },
  },
  "vendor-pending-review": {
    token: "dev-token-vendor-review",
    user: {
      id: "dev-vendor-003",
      email: "vendor-review@example.com",
      firstName: "Mei",
      lastName: "Chen",
      roles: ["vendor"] as UserRole[],
      vendorStatus: "pending_review" as VendorStatus,
      applicationId: "dev-app-003",
      temporaryPasswordSet: false,
    },
  },
  "vendor-approved": {
    token: "dev-token-vendor-approved",
    user: {
      id: "dev-vendor-004",
      email: "vendor-approved@example.com",
      firstName: "Jun",
      lastName: "Liu",
      roles: ["vendor"] as UserRole[],
      vendorStatus: "approved" as VendorStatus,
      applicationId: "dev-app-004",
      temporaryPasswordSet: false,
    },
  },
  "vendor-rejected": {
    token: "dev-token-vendor-rejected",
    user: {
      id: "dev-vendor-005",
      email: "vendor-rejected@example.com",
      firstName: "Fang",
      lastName: "Zhao",
      roles: ["vendor"] as UserRole[],
      vendorStatus: "rejected" as VendorStatus,
      applicationId: "dev-app-005",
      temporaryPasswordSet: false,
    },
  },
  staff: {
    token: "dev-token-staff",
    user: {
      id: "dev-staff-001",
      email: "staff@example.com",
      firstName: "Alex",
      lastName: "Kim",
      roles: ["staff"] as UserRole[],
      vendorStatus: null,
      applicationId: null,
      temporaryPasswordSet: false,
    },
  },
};

// The entire (auth) group is public — never bootstrap while any auth
// screen (login, register, otp-verification, etc.) is active.
const PUBLIC_ROUTE_SEGMENTS = ["(auth)", "login", "register", "get-started"];
const VENDOR_PROFILES = [
  "vendor-landing",
  "vendor-in-progress",
  "vendor-pending-review",
  "vendor-approved",
  "vendor-rejected",
];
const STAFF_PROFILES = ["staff"];

/**
 * Renders nothing — just calls `login()` once on mount with the active dev profile.
 * Drop this inside <AuthProvider> in _layout.tsx, gated by __DEV__.
 */
export function DevAuthBootstrap() {
  const segments = useSegments();
  const { login, isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    // Wait until the real session has been read from storage before deciding
    // whether to bootstrap. Without this, the dev profile fires immediately
    // on mount (isAuthenticated: false) and overrides a valid stored session,
    // sending staff deep-links to the wrong dashboard.
    if (isLoading) return;

    // Stop re-injecting if the user explicitly logged out via the dev button.
    if (devAuthSuppressed) return;

    const isInPublicRoute = segments.some((s) => PUBLIC_ROUTE_SEGMENTS.includes(String(s)));

    const vendorCondition =
      VENDOR_PROFILES.includes(DEV_ACTIVE_PROFILE) && !isAuthenticated && !isInPublicRoute;

    const staffCondition =
      STAFF_PROFILES.includes(DEV_ACTIVE_PROFILE) && !isAuthenticated && !isInPublicRoute;

    if (vendorCondition || staffCondition) {
      const profile = DEV_PROFILES[DEV_ACTIVE_PROFILE];

      login(profile.token, profile.user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments.join("/"), isAuthenticated, isLoading]);

  return null;
}
